import { memo } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { CreateVoucherRequest } from '../../types';
import { safeStringify } from '../../utils';

interface VoucherFormDebugPanelProps {
  form: UseFormReturn<CreateVoucherRequest>;
  currentStep: number;
}

const debugStyles = {
  container: {
    position: 'fixed' as const,
    top: '10px',
    right: '10px',
    background: '#f7fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '12px',
    maxWidth: '300px',
    maxHeight: '400px',
    overflow: 'auto',
    fontSize: '12px',
    zIndex: 9999,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  button: {
    padding: '4px 8px',
    fontSize: '10px',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
} as const;

const DebugPanelComponent = ({
  form,
  currentStep,
}: VoucherFormDebugPanelProps) => {
  if (process.env.NODE_ENV === 'production') return null;

  const { formState } = form;
  const errorCount = Object.keys(formState.errors).length;

  return (
    <div style={debugStyles.container}>
      <div
        style={{ fontWeight: 'bold', marginBottom: '8px', color: '#2d3748' }}
      >
        <span role="img" aria-label="bug">
          🐛
        </span>{' '}
        Form Debug Panel
      </div>
      <div style={{ marginBottom: '8px' }}>
        <strong>Step:</strong> {currentStep} | <strong>Valid:</strong>{' '}
        {formState.isValid ? '✅' : '❌'}
      </div>
      <div style={{ marginBottom: '8px' }}>
        <strong>Errors:</strong> {errorCount}
      </div>
      <div style={{ marginBottom: '8px' }}>
        <strong>Dirty:</strong> {formState.isDirty ? '✅' : '❌'} |
        <strong>Touched:</strong> {Object.keys(formState.touchedFields).length}
      </div>

      {errorCount > 0 && (
        <details style={{ marginBottom: '8px' }}>
          <summary style={{ cursor: 'pointer', color: '#e53e3e' }}>
            View Errors
          </summary>
          <pre style={{ fontSize: '10px', margin: '4px 0', color: '#e53e3e' }}>
            {safeStringify(formState.errors)}
          </pre>
        </details>
      )}

      <details>
        <summary style={{ cursor: 'pointer', color: '#3182ce' }}>
          View Form Data
        </summary>
        <pre style={{ fontSize: '10px', margin: '4px 0', color: '#2d3748' }}>
          {safeStringify(form.getValues())}
        </pre>
      </details>

      <div style={{ marginTop: '8px', display: 'flex', gap: '4px' }}>
        <button
          type="button"
          onClick={async () => {
            const isValid = await form.trigger();
            console.log('🔍 Manual validation trigger', {
              isValid,
              errors: form.formState.errors,
            });
          }}
          style={{ ...debugStyles.button, background: '#3182ce' }}
        >
          Validate All
        </button>
        <button
          type="button"
          onClick={() => {
            console.log('📋 Current form state:', {
              data: form.getValues(),
              errors: form.formState.errors,
              valid: form.formState.isValid,
            });
          }}
          style={{ ...debugStyles.button, background: '#38a169' }}
        >
          Log State
        </button>
      </div>
    </div>
  );
};

export const VoucherFormDebugPanel = memo(DebugPanelComponent);
